import { Controller } from "@hotwired/stimulus";

interface MediaMeta {
  id: string;
  mime_type: string;
  size_bytes: number;
  retention: string;
  storage_key: string | null;
  expires_at: string | null;
  derived_from: string | null;
  url: string | null;
}

/**
 * Detects <lumenfall-media /> tags inside JSON body <pre> blocks,
 * highlights them as clickable pills, and opens a lightbox with image
 * preview + metadata on click.
 *
 * Usage:
 *   <div data-controller="admin-media-tags"
 *        data-admin-media-tags-media-value='{"med_xxx": {...}}'>
 *     <pre data-admin-media-tags-target="body">...</pre>
 *   </div>
 */
export default class extends Controller {
  static values = { media: Object };
  static targets = ["body"];

  declare mediaValue: Record<string, MediaMeta>;
  declare bodyTargets: HTMLPreElement[];

  private dialog: HTMLDialogElement | null = null;
  private tagUrls: Record<string, string> = {};

  connect() {
    this.bodyTargets.forEach((pre) => this.highlightTags(pre));
    this.buildDialog();
  }

  disconnect() {
    this.dialog?.remove();
    this.dialog = null;
  }

  private highlightTags(pre: HTMLPreElement) {
    // Inside <pre>, ERB HTML-escapes content. The browser then parses entities
    // into text and re-serializes innerHTML. Key: innerHTML keeps &lt; &gt; &amp;
    // but renders &quot; as literal " characters. So in innerHTML, tags look like:
    //   &lt;lumenfall-media id=\"med_xxx\" ... /&gt;
    // (backslash + literal quote, NOT &quot;)
    const html = pre.innerHTML;

    // Match &lt;lumenfall-media ... /&gt;
    const tagPattern = /&lt;lumenfall-media\s[\s\S]*?\/&gt;/g;

    const replaced = html.replace(tagPattern, (match) => {
      // Extract media ID — quotes appear as \" (backslash + literal) in innerHTML
      const idMatch = match.match(
        /id=\\?"(med_[A-Za-z0-9]+)\\?"/,
      );
      if (!idMatch) return match;

      const mediaId = idMatch[1];
      const meta = this.mediaValue[mediaId];

      // Extract original_url if present in the tag
      const urlMatch = match.match(
        /original_url=\\?"(https?:\/\/[^"]+?)\\?"/,
      );
      const tagUrl = urlMatch ? urlMatch[1] : null;

      // Prefer tag URL (environment-correct) over DB URL (production domain)
      const imageUrl = tagUrl || meta?.url;
      if (tagUrl) this.tagUrls[mediaId] = tagUrl;

      const hasImage = !!imageUrl;

      return (
        `<button type="button" class="admin-media-tag ${hasImage ? "has-image" : "no-image"}" ` +
        `data-media-id="${mediaId}" ` +
        `data-action="click->admin-media-tags#openLightbox" ` +
        `title="Click to inspect media ${mediaId}">` +
        `<span class="admin-media-tag__icon">${hasImage ? "&#x1F5BC;" : "&#x1F4CE;"}</span>` +
        `<span class="admin-media-tag__id">${mediaId}</span>` +
        `</button>`
      );
    });

    if (replaced !== html) {
      pre.innerHTML = replaced;
    }
  }

  private buildDialog() {
    this.dialog = document.createElement("dialog");
    this.dialog.className = "modal";
    this.dialog.addEventListener("click", (e) => {
      if (e.target === this.dialog) this.dialog?.close();
    });
    document.body.appendChild(this.dialog);
  }

  openLightbox(event: Event) {
    const btn = (event.target as HTMLElement).closest(
      "[data-media-id]",
    ) as HTMLElement;
    if (!btn || !this.dialog) return;

    const mediaId = btn.dataset.mediaId!;
    const meta = this.mediaValue[mediaId];
    // Prefer the original_url from the tag (correct for current env) over the DB-derived URL
    const imageUrl = this.tagUrls[mediaId] || meta?.url || null;

    this.dialog.innerHTML = this.renderLightboxContent(
      mediaId,
      meta,
      imageUrl,
    );
    this.dialog.showModal();
  }

  private renderLightboxContent(
    mediaId: string,
    meta: MediaMeta | undefined,
    imageUrl: string | null,
  ): string {
    const sizeFormatted = meta
      ? this.formatBytes(meta.size_bytes)
      : "Unknown";
    const retentionLabels: Record<string, string> = {
      transient: "Transient (~1hr)",
      week: "Week (7 days)",
      month: "Month (30 days)",
      persistent: "Persistent",
    };
    const retention = meta
      ? retentionLabels[meta.retention] || meta.retention
      : "Unknown";
    const expiresAt = meta?.expires_at
      ? new Date(meta.expires_at).toLocaleString()
      : "—";
    const mimeType = meta?.mime_type || "Unknown";
    const derivedFrom = meta?.derived_from || null;

    return `
      <div class="modal-box max-w-3xl p-0 bg-base-100 overflow-hidden">
        <div class="flex items-center justify-between px-5 py-3 bg-base-200 border-b border-base-300">
          <div class="flex items-center gap-2">
            <span class="font-mono text-sm font-semibold">${mediaId}</span>
            <span class="badge badge-sm badge-info">${mimeType}</span>
          </div>
          <button type="button" onclick="this.closest('dialog').close()"
                  class="btn btn-sm btn-circle btn-ghost">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                 fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                 stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>

        ${
          imageUrl
            ? `
        <div class="bg-[repeating-conic-gradient(oklch(0.85_0_0)_0%_25%,white_0%_50%)] bg-[length:20px_20px] flex items-center justify-center p-4 max-h-[60vh] overflow-auto">
          <img src="${imageUrl}" alt="Media ${mediaId}"
               class="max-w-full max-h-[55vh] object-contain rounded shadow-lg"
               loading="lazy" />
        </div>
        `
            : `
        <div class="flex items-center justify-center p-12 bg-base-200 text-base-content/40">
          <div class="text-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24"
                 fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round"
                 stroke-linejoin="round" class="mx-auto mb-3 opacity-40">
              <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/>
              <circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
            </svg>
            <p class="text-sm">Image not available</p>
            <p class="text-xs mt-1">Storage key missing or media expired</p>
          </div>
        </div>
        `
        }

        <div class="px-5 py-4 border-t border-base-300">
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <div class="text-xs text-base-content/50">Size</div>
              <div class="font-mono text-sm mt-0.5">${sizeFormatted}</div>
            </div>
            <div>
              <div class="text-xs text-base-content/50">Retention</div>
              <div class="text-sm mt-0.5">${retention}</div>
            </div>
            <div>
              <div class="text-xs text-base-content/50">Expires</div>
              <div class="text-sm mt-0.5">${expiresAt}</div>
            </div>
            <div>
              <div class="text-xs text-base-content/50">Storage Key</div>
              <div class="font-mono text-xs mt-0.5 truncate" title="${meta?.storage_key || "—"}">${meta?.storage_key || "—"}</div>
            </div>
          </div>
          ${
            derivedFrom
              ? `
          <div class="mt-3 pt-3 border-t border-base-200">
            <div class="text-xs text-base-content/50">Derived From</div>
            <div class="font-mono text-sm mt-0.5">${derivedFrom}</div>
          </div>
          `
              : ""
          }
          ${
            imageUrl
              ? `
          <div class="mt-3 pt-3 border-t border-base-200">
            <a href="${imageUrl}" target="_blank" rel="noopener noreferrer"
               class="btn btn-sm btn-ghost gap-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                   fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                   stroke-linejoin="round"><path d="M21 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6"/>
                <path d="m21 3-9 9"/><path d="M15 3h6v6"/></svg>
              Open in new tab
            </a>
          </div>
          `
              : ""
          }
        </div>
      </div>
      <form method="dialog" class="modal-backdrop bg-black/70">
        <button>close</button>
      </form>
    `;
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
  }
}
